import { evas2ReportServices, isRetryableRequestError } from '@/services/evas2'
import {
  classifyEvas2ReportAggregate,
  EVAS2_REPORT_AGGREGATE_STATUS,
  EVAS2_REPORT_WAIT_TIMEOUT_MS,
} from '@/utils/evas2ReportAggregate'

export const REPORT_FINALIZATION_STORAGE_KEY = 'evas2.report-finalization.v1'
export const REPORT_FINALIZATION_POLL_MS = 2000

let pollTimer = null
let pollInFlight = false
let trackingEpoch = 0

const initialState = () => ({
  active: false,
  sessionId: '',
  examinationId: '',
  accNo: '',
  startedAtMs: null,
  waitStartedAtMs: null,
  stage: 'finalizing',
  message: '正在完成全文定稿',
  terminalStatus: null,
  terminalMessage: '',
})

function normalizeIdentity(payload = {}) {
  const sessionId = String(payload.sessionId || '').trim()
  const examinationId = String(payload.examinationId || '').trim()
  const accNo = String(payload.accNo || '').trim()
  const startedAtMs = Number(payload.startedAtMs)
  const waitStartedAtMs = Number(payload.waitStartedAtMs)
  const stage = ['finalizing', 'reporting', 'syncing', 'confirming'].includes(payload.stage)
    ? payload.stage
    : 'finalizing'
  const terminalStatus = payload.terminalStatus === 'timeout' ? 'timeout' : null
  if (!sessionId || !examinationId) return null
  return {
    version: 1,
    sessionId,
    examinationId,
    accNo,
    startedAtMs: Number.isFinite(startedAtMs) && startedAtMs > 0 ? startedAtMs : Date.now(),
    waitStartedAtMs: Number.isFinite(waitStartedAtMs) && waitStartedAtMs > 0
      ? waitStartedAtMs
      : (Number.isFinite(startedAtMs) && startedAtMs > 0 ? startedAtMs : Date.now()),
    stage,
    terminalStatus,
    terminalMessage: terminalStatus ? String(payload.terminalMessage || '') : '',
    timedOutAtMs: terminalStatus && Number.isFinite(Number(payload.timedOutAtMs))
      ? Number(payload.timedOutAtMs)
      : null,
  }
}

function valuesAreCompatible(expected, actual) {
  const left = String(expected || '').trim()
  const right = String(actual || '').trim()
  return !left || !right || left === right
}

export function reportFinalizationIdentityMatches(marker, identity = {}) {
  if (!marker) return false
  return String(marker.sessionId || '') === String(identity.sessionId || '')
    && String(marker.examinationId || '') === String(identity.examinationId || '')
    && valuesAreCompatible(marker.accNo, identity.accNo)
}

function saveMarker(marker) {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(REPORT_FINALIZATION_STORAGE_KEY, JSON.stringify(marker))
}

function removeMarker() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(REPORT_FINALIZATION_STORAGE_KEY)
}

export function readReportFinalizationMarker() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    return normalizeIdentity(JSON.parse(sessionStorage.getItem(REPORT_FINALIZATION_STORAGE_KEY) || 'null'))
  } catch (_) {
    removeMarker()
    return null
  }
}

function reportRouteIdentity(route) {
  if (!route || route.name !== 'Report2Detail') return null
  const sessionId = String((route.query || {}).session || '').trim()
  const examinationId = String((route.params || {}).examinationID || '').trim()
  const accNo = String((route.params || {}).accNo || '').trim()
  return sessionId && examinationId ? { sessionId, examinationId, accNo } : null
}

/** canonical report row 的終態優先於 KKASR runtime lifecycle，避免舊失敗蓋掉可覆核草稿。 */
export function deriveReportFinalizationObservation(payload, identity) {
  const body = payload && typeof payload === 'object' ? payload : {}
  const expected = typeof identity === 'string' ? { sessionId: identity } : (identity || {})
  const identityVerified = body.found === true
    ? String(body.kkasrSessionId || '') === String(expected.sessionId || '')
      && valuesAreCompatible(expected.accNo, body.accNo)
    : String(body.requestedSessionId || '') === String(expected.sessionId || '')
  const aggregate = classifyEvas2ReportAggregate(body, { identityVerified })

  if (body.found === true && identityVerified) {
    if ([
      EVAS2_REPORT_AGGREGATE_STATUS.ACCEPTED,
      EVAS2_REPORT_AGGREGATE_STATUS.NEEDS_REVIEW,
    ].includes(aggregate.status)) {
      return { terminal: 'ready', stage: 'ready', message: '報告草稿已可覆核' }
    }
    if (aggregate.status === EVAS2_REPORT_AGGREGATE_STATUS.FAILED) {
      return { terminal: 'failed', stage: 'failed', message: '報告處理失敗，請重新載入查詢狀態' }
    }
    return { terminal: null, stage: 'reporting', message: '正在整理報告' }
  }

  if (!identityVerified) {
    return { terminal: null, stage: 'confirming', message: '正在確認本次處理狀態' }
  }
  const lifecycle = String(body.sessionLifecycle || '')
  if (lifecycle === 'asr_finalizing' || lifecycle === 'asr_active') {
    return { terminal: null, stage: 'finalizing', message: '正在完成全文定稿' }
  }
  if (lifecycle === 'llm_running') {
    return { terminal: null, stage: 'reporting', message: '正在整理報告' }
  }
  if (lifecycle === 'completed') {
    return { terminal: null, stage: 'syncing', message: '正在同步本次報告' }
  }
  if (['failed', 'aborted'].includes(lifecycle)) {
    return { terminal: 'failed', stage: 'failed', message: '本次辨識或報告處理失敗，請重新載入查詢狀態' }
  }
  return { terminal: null, stage: 'confirming', message: '正在確認本次處理狀態' }
}

const state = initialState()

const getters = {
  active: (s) => s.active,
  identity: (s) => ({
    sessionId: s.sessionId,
    examinationId: s.examinationId,
    accNo: s.accNo,
  }),
  startedAtMs: (s) => s.startedAtMs,
  waitStartedAtMs: (s) => s.waitStartedAtMs,
  stageMessage: (s) => s.message,
  terminal: (s) => ({ status: s.terminalStatus, message: s.terminalMessage }),
}

const mutations = {
  begin(s, marker) {
    Object.assign(s, initialState(), marker, {
      active: true,
      message: marker.stage === 'reporting'
        ? '正在整理報告'
        : (marker.stage === 'syncing'
          ? '正在同步本次報告'
          : (marker.stage === 'confirming' ? '正在確認本次處理狀態' : '正在完成全文定稿')),
    })
  },
  observe(s, observation) {
    const stageOrder = { finalizing: 0, confirming: 0, reporting: 1, syncing: 2 }
    if (
      !observation.terminal
      && stageOrder[observation.stage] < stageOrder[s.stage]
    ) return
    if (
      ['finalizing', 'confirming'].includes(s.stage)
      && ['reporting', 'syncing'].includes(observation.stage)
    ) {
      // 既有四分鐘上限屬報告 publication gate。ASR 全文定稿本身可能接近四分鐘，
      // backend 明確進到 LLM／同步後才開始計算報告等待時間。
      s.waitStartedAtMs = Date.now()
    }
    s.stage = observation.stage
    s.message = observation.message
  },
  finish(s, { status, message }) {
    s.active = false
    s.terminalStatus = status
    s.terminalMessage = message || ''
  },
  restoreTerminal(s, marker) {
    Object.assign(s, initialState(), marker, {
      active: false,
      terminalStatus: marker.terminalStatus,
      terminalMessage: marker.terminalMessage || '',
    })
  },
  setAccNo(s, accNo) {
    s.accNo = String(accNo || '').trim()
  },
}

const actions = {
  begin({ commit, dispatch }, payload) {
    const marker = normalizeIdentity(payload)
    if (!marker) return false
    trackingEpoch += 1
    dispatch('stopPolling')
    saveMarker(marker)
    commit('begin', marker)
    dispatch('startPolling')
    return true
  },

  resume({ state: s, commit, dispatch }) {
    if (s.active) return true
    const marker = readReportFinalizationMarker()
    if (!marker) return false
    if (marker.terminalStatus === 'timeout') {
      commit('restoreTerminal', marker)
      return true
    }
    return dispatch('begin', marker)
  },

  initialize({ state: s, commit, dispatch }, route) {
    const routeIdentity = reportRouteIdentity(route)
    if (!routeIdentity) return dispatch('resume')
    if (reportFinalizationIdentityMatches(s, routeIdentity)
      && (s.active || s.terminalStatus === 'timeout')) return true

    const persisted = readReportFinalizationMarker()
    if (!reportFinalizationIdentityMatches(persisted, routeIdentity)) {
      return dispatch('begin', {
        ...routeIdentity,
        startedAtMs: Date.now(),
      })
    }

    // 初次掛載要在 polling 前先選定 URL identity；同場才沿用跨 F5 的 deadline。
    const selected = {
      ...persisted,
      accNo: persisted.accNo || routeIdentity.accNo,
    }
    if (selected.terminalStatus === 'timeout') {
      saveMarker(selected)
      commit('restoreTerminal', selected)
      return true
    }
    return dispatch('begin', selected)
  },

  adoptRoute({ state: s, commit, dispatch }, route) {
    const routeIdentity = reportRouteIdentity(route)
    if (!routeIdentity) return false
    const { sessionId, examinationId, accNo } = routeIdentity
    const sameIdentity = reportFinalizationIdentityMatches(s, routeIdentity)
    if (sameIdentity && (s.active || s.terminalStatus === 'timeout')) {
      // 舊 marker 可能沒有 accNo。明確 Report2 route 可補齊 identity，但不能重開
      // polling/deadline；後續 canonical 回應便必須通過同一病例號。
      if (!s.accNo && accNo) {
        commit('setAccNo', accNo)
        saveMarker({
          version: 1,
          sessionId: s.sessionId,
          examinationId: s.examinationId,
          accNo: s.accNo,
          startedAtMs: s.startedAtMs,
          waitStartedAtMs: s.waitStartedAtMs,
          stage: s.stage,
          terminalStatus: s.terminalStatus,
          terminalMessage: s.terminalMessage,
        })
      }
      return true
    }
    return dispatch('begin', {
      sessionId,
      examinationId,
      accNo,
      startedAtMs: Date.now(),
    })
  },

  observe({ state: s, commit, dispatch }, payload) {
    const queryOnlyAfterTimeout = !s.active && s.terminalStatus === 'timeout'
    if (!s.active && !queryOnlyAfterTimeout) return null
    const observation = deriveReportFinalizationObservation(payload, {
      sessionId: s.sessionId,
      accNo: s.accNo,
    })
    if (queryOnlyAfterTimeout && !observation.terminal) return observation
    commit('observe', observation)
    if (observation.terminal) {
      dispatch('release', { status: observation.terminal, message: observation.message })
    } else {
      saveMarker({
        sessionId: s.sessionId,
        examinationId: s.examinationId,
        accNo: s.accNo,
        startedAtMs: s.startedAtMs,
        waitStartedAtMs: s.waitStartedAtMs,
        stage: s.stage,
      })
    }
    return observation
  },

  release({ state: s, commit, dispatch }, { status = 'failed', message = '' } = {}) {
    trackingEpoch += 1
    if (status === 'timeout') {
      saveMarker({
        version: 1,
        sessionId: s.sessionId,
        examinationId: s.examinationId,
        accNo: s.accNo,
        startedAtMs: s.startedAtMs,
        waitStartedAtMs: s.waitStartedAtMs,
        stage: s.stage,
        terminalStatus: 'timeout',
        terminalMessage: message,
        timedOutAtMs: Date.now(),
      })
    } else {
      removeMarker()
    }
    dispatch('stopPolling')
    commit('finish', { status, message })
  },

  startPolling({ state: s, dispatch }) {
    if (!s.active || pollTimer) return
    dispatch('pollOnce')
    pollTimer = window.setInterval(() => dispatch('pollOnce'), REPORT_FINALIZATION_POLL_MS)
  },

  stopPolling() {
    if (pollTimer) window.clearInterval(pollTimer)
    pollTimer = null
  },

  async pollOnce({ state: s, dispatch }) {
    if (!s.active || pollInFlight) return null
    if (Date.now() - Number(s.waitStartedAtMs || s.startedAtMs || 0) >= EVAS2_REPORT_WAIT_TIMEOUT_MS) {
      dispatch('release', {
        status: 'timeout',
        message: '等待已超過 4 分鐘，結果仍待確認；可重新載入查詢本次報告，不會重送結束指令。',
      })
      return null
    }
    const epoch = trackingEpoch
    const sessionId = s.sessionId
    const examinationId = s.examinationId
    pollInFlight = true
    try {
      const body = await evas2ReportServices.fetch(examinationId, { sessionId })
      if (epoch !== trackingEpoch || !s.active || s.sessionId !== sessionId) return null
      return dispatch('observe', body)
    } catch (err) {
      if (epoch !== trackingEpoch || !s.active || s.sessionId !== sessionId) return null
      if (!isRetryableRequestError(err)) {
        dispatch('release', {
          status: 'failed',
          message: '目前無法核對本次報告狀態，請重新載入查詢；不會重送結束指令。',
        })
      }
      return null
    } finally {
      pollInFlight = false
    }
  },
}

export default { namespaced: true, state, getters, mutations, actions }
